import { examOptions, mockOutOfPocket } from '@/pages/Web/proj/tongxin-demo/mockData/examOptions'
import { mockInfo } from '@/pages/Web/proj/tongxin-demo/mockData/info'
import { addOnOptions } from '@/pages/Web/proj/tongxin-demo/mockData/addOns'
import { mockTime } from '@/pages/Web/proj/tongxin-demo/mockData/mockTime.js'

// this file demosntrates how step machine is set up to guide page navigation
// computed property, step nodes, function calls can be extracted into individual service functions
// this file currently stores user's selection in localstorage

export default {
  data() {
    return {
      // Step configuration -replace with API data in the future
      nodes: {},
      context: {},
      localValue: null,
      isLoading: false,
    }
  },

  computed: {
    currentNode() {
      return this.nodes[this.stepId] || null
    },

    resolvedProps() {
      if (!this.currentNode) return {}
      const p = this.currentNode.props
      return typeof p === 'function' ? p(this.context) : (p || {})
    },

    componentProps() {
      if (!this.currentNode) return {}
      return this.currentNode.usesValue
        ? Object.assign({}, this.resolvedProps, { value: this.localValue })
        : this.resolvedProps
    },

    canProceed() {
      const node = this.currentNode
      if (!node) return false

      const result = typeof node.validate === 'function'
        ? node.validate(this.context, this.localValue)
        : true

      return result
    },

    textForNextStep() {
      return this.currentNode?.textForNextStep || '下一步'
    },

    selectedExamTypeText() {
      if (!this.context.examType) return '未知體檢類型';
      const examTypeOption = examOptions.find(opt => opt.value === this.context.examType);
      return examTypeOption ? examTypeOption.text : '未知體檢類型';
    },

    selectedPlanBaseFee() {
      if (!this.context.outOfPocketPlan) return 0;

      const planOption = mockOutOfPocket.find(plan => plan.value === this.context.outOfPocketPlan);
      if (!planOption) return 0;

      // Extract numeric price from description (e.g., "2000 元" -> 2000)
      const priceMatch = planOption.description.match(/(\d+)/);
      return priceMatch ? parseInt(priceMatch[1]) : 0;
    },

    mappedAddOnOptions() {
      return addOnOptions.map(o => ({
        label: o.label,
        value: o.id,
        right_display_info: `${o.price} 元`,
        detailed_info: o.info || [],
        expandable: Array.isArray(o.info) && o.info.length > 0,
        price: o.price
      }));
    },

    mappedExamOptions() {
      return examOptions.map(o => ({
        label: o.text,
        value: o.value,
        description: o.description,
        icon: o.icon,
        color: o.color,
      }));
    },

    mappedOutOfPocketOptions() {
      return mockOutOfPocket.map(o => ({
        label: o.text,
        value: o.value,
        description: o.description,
        icon: o.icon,
        color: o.color,
      }));
    },
  },

  methods: {
    // Initialize step nodes from mock data (will be replaced with API call)
    initializeMockSteps() {
      this.nodes = {
        'exam-selection': {
          component: 'SelectionStep',
          usesValue: true,
          props: () => ({
            title: '請選擇體檢 / 健檢種類',
            items: this.mappedExamOptions,
          }),
          initValue: (ctx) => ctx.examType || null,
          validate: (ctx, val) => !!val,
          onNext: (ctx, selected) => {
            Object.assign(ctx, { examType: selected })
            if (selected === 'out-of-pocket') return 'oop-selection'
            alert(selected)
            return null
          },
        },
        'oop-selection': {
          component: 'SelectionStep',
          usesValue: true,
          props: () => ({
            title: '請選擇加值自費方案',
            items: this.mappedOutOfPocketOptions,
          }),
          initValue: (ctx) => ctx.outOfPocketPlan || null,
          validate: (ctx, val) => !!val,
          onBack: () => 'exam-selection',
          onNext: (ctx, selected) => {
            Object.assign(ctx, { outOfPocketPlan: selected })
            const nextStep = this.getOutOfPocketNextStep(selected);
            if (nextStep) return nextStep;
            alert(selected)
            return null
          },
        },
        'info-teen': {
          component: 'Info',
          usesValue: false,
          props: () => ({ data: mockInfo }),
          onBack: () => 'oop-selection',
          onNext: () => 'add-ons',
        },
        'info-adult': {
          component: 'Info',
          usesValue: false,
          props: () => ({ data: mockInfo }),
          onBack: () => 'oop-selection',
          onNext: () => 'add-ons',
        },
        'info-senior': {
          component: 'Info',
          usesValue: false,
          props: () => ({ data: mockInfo }),
          onBack: () => 'oop-selection',
          onNext: () => 'add-ons',
        },
        'add-ons': {
          component: 'AddOn',
          usesValue: true,
          initValue: (ctx) => Array.isArray(ctx.addOns) ? ctx.addOns.slice() : [],
          props: () => ({
            baseFee: this.selectedPlanBaseFee,
            options: this.mappedAddOnOptions,
          }),
          validate: () => true,
          onBack: () => this.getAddOnsBackStep(),
          onNext: (ctx, selected) => {
            Object.assign(ctx, { addOns: Array.isArray(selected) ? selected.slice() : [] })
            return 'notice'
          },
        },
        'notice': {
          component: 'Notice',
          usesValue: false,
          onBack: () => 'add-ons',
          onNext: () => 'date-selection',
          textForNextStep: '我已瞭解',
        },
        'date-selection': {
          component: 'TongxinDateSelector',
          usesValue: true,
          initValue: (ctx) => ctx.selectedDate || null,
          props: () => ({
            offDutyDates: ['2025-08-27', '2025-08-16', '2025-08-19'],
            recurringOffDays: [0],
            bookingWindow: {type: 'day', amount: 60}
          }),
          validate: (ctx, val) => !!val, // Must select a date to proceed
          onBack: () => 'notice',
          onNext: (ctx, selected) => {
            Object.assign(ctx, { selectedDate: selected })
            return 'time-selection'
          },
        },
        'time-selection': {
          component: 'AppointmentTimeSelector',
          usesValue: true,
          initValue: (ctx) => ctx.selectedTimeSlot || null,
          props: () => ({
            dates: mockTime,
            loading: false,
            selectedDate: this.context.selectedDate // Pass the selected date
          }),
          validate: (ctx, val) => !!val, // Must select a time slot to proceed
          onBack: () => 'date-selection',
          onNext: (ctx, selected) => {
            Object.assign(ctx, { selectedTimeSlot: selected })
            return 'confirmation' // You can add a confirmation step here
          },
        },
        'confirmation': {
          component: 'Confirmation',
          usesValue: false,
          props: () => ({
            examTypeText: this.selectedExamTypeText,
            visitTimeSlot: this.context.selectedTimeSlot,
            totalCost: this.selectedPlanBaseFee + this.calculateAddOnsCost(),
          }),
          onBack: () => 'time-selection',
          onNext: (ctx) => {
            console.log(ctx)
            // This could be where you submit the appointment
            // For now, just show success message
            alert('預約成功！');
            return 'success'
          },
          textForNextStep: '確認預約',
        },
        'success': {
          component: 'Success',
          usesValue: false,
          props: () => ({
            examTypeText: this.selectedExamTypeText,
            visitTimeSlot: this.context.selectedTimeSlot,
            totalCost: this.selectedPlanBaseFee + this.calculateAddOnsCost(),
          }),
          onBack: () => 'confirmation',
          onNext: (ctx) => {
            console.log(ctx)
            alert('返回 OA');
            return null;
          },
          backButtonIsVisible: false,
          textForNextStep: '回 LINE 官方帳號',
        },
      }
    },

    // Future method: Load steps from API
    async loadStepsFromAPI() {
      try {
        this.isLoading = true
        // TODO: Replace with actual API call
        // const response = await this.$api.tongxin.getSteps()
        // this.nodes = this.transformAPIStepsToNodes(response.data)

        // For now, fall back to mock data
        this.initializeMockSteps()
      } catch (error) {
        console.error('Failed to load steps from API:', error)
        // Fall back to mock data on error
        this.initializeMockSteps()
      } finally {
        this.isLoading = false
      }
    },


    // transformAPIStepsToNodes(apiSteps) {
      // TODO: Transform API response to match the expected node structure
      // This will depend on your API response format
    // },

    // Step navigation methods
    onValueChange(v) {
      this.localValue = v;
      // Force reactivity update
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },

    enterNode(id) {
      const node = this.nodes[id]
      if (node && typeof node.initValue === 'function') {
        this.localValue = node.initValue(this.context)
      } else {
        this.localValue = node && node.usesValue ? null : null
      }

      // Force reactivity update after setting localValue
      this.$nextTick(() => {
        if (this.localValue !== undefined) {
          this.$forceUpdate()
        }
      })
    },

    handleNext() {
      const node = this.currentNode
      if (!node || !node.onNext || !this.canProceed) return

      const next = node.onNext(this.context, this.localValue)
      if (typeof next === 'string') {
        // Force reactivity update and save
        this.$set(this, 'context', { ...this.context })
        this.saveContext()

        this.$router.push({
          name: 'TongxinDemoStep',
          params: {
            ...this.$route.params,
            stepId: next
          }
        })
        this.localValue = null
      }
    },

    handleBack() {
      const node = this.currentNode
      if (node && typeof node.onBack === 'function') {
        const prev = node.onBack(this.context)
        if (typeof prev === 'string') {
          this.$router.push({
            name: 'TongxinDemoStep',
            params: {
              ...this.$route.params,
              stepId: prev
            }
          })
        }
      }
    },

    // Context persistence
    saveContext() {
      try {
        // Save both context and current localValue
        const dataToSave = {
          context: this.context,
          currentStepId: this.stepId,
          localValue: this.localValue
        };
        localStorage.setItem('tongxin-context', JSON.stringify(dataToSave))
      } catch (e) {
        console.warn('Failed to save context to localStorage:', e)
      }
    },

    loadContext() {
      try {
        const saved = localStorage.getItem('tongxin-context')
        if (saved) {
          const parsed = JSON.parse(saved)
          // Load context
          if (parsed.context) {
            this.context = parsed.context
          }
          // restore localValue if we're on the same step
          if (parsed.currentStepId === this.stepId && parsed.localValue !== undefined) {
            this.localValue = parsed.localValue
          }
        }
      } catch (e) {
        console.warn('Failed to load context from localStorage:', e)
      }
    },

    // getAvailableDates() {
    //   // TODO: This could come from an API call based on the selected package
    // },


    // getConfirmationData() {
    //   // TODO: Implement confirmation data logic
    // },


    // async submitAppointment() {
    //   // TODO: Replace with actual API call
    // },


    calculateAddOnsCost() {
      if (!Array.isArray(this.context.addOns)) return 0;

      return this.context.addOns.reduce((total, addonId) => {
        const addonOption = addOnOptions.find(option => option.id === addonId);
        return total + (addonOption ? addonOption.price : 0);
      }, 0);
    },

    // Simplify confirmation props calculation
    calculateConfirmationProps(ctx) {
      return {
        examTypeText: this.selectedExamTypeText,
        visitTimeSlot: ctx.selectedTimeSlot,
        totalCost: this.selectedPlanBaseFee + this.calculateAddOnsCost(),
      };
    },

    getAddOnsBackStep() {
      const ctx = this.context;
      if (ctx.outOfPocketPlan === 'teen-package') return 'info-teen';
      if (ctx.outOfPocketPlan === 'adult-male') return 'info-adult';
      if (ctx.outOfPocketPlan === 'senior-package') return 'info-senior';
      return 'oop-selection';
    },

    getOutOfPocketNextStep(selected) {
      if (selected === 'teen-package') return 'info-teen';
      if (selected === 'adult-male') return 'info-adult';
      if (selected === 'senior-package') return 'info-senior';
      return null;
    },
  },

  // Lifecycle hooks
  watch: {
    stepId: {
      immediate: true,
      handler(newStepId) {
        this.enterNode(newStepId)
      }
    },
    context: {
      deep: true,
      handler() {
        this.saveContext();
      }
    },
    localValue: {
      handler() {
        this.saveContext();
      }
    }
  },

  mounted() {
    this.loadContext()
    // TODO: Replace with API call when ready
    this.initializeMockSteps()
    // this.loadStepsFromAPI()
  },

  beforeDestroy() {
    this.saveContext()
  },
}
